import { useState, ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, X, Edit2, ShieldAlert } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { extractErr } from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Approval = {
  id: number
  run: number
  agent_name: string
  tool: number
  tool_code: string
  tool_label: string
  tool_risk_level: 'SAFE' | 'WRITE' | 'EXTERNAL'
  proposed_input: Record<string, any>
  reasoning: string
  status: 'PENDING' | 'APPROVED' | 'EDITED' | 'REJECTED' | 'EXPIRED'
  final_input: Record<string, any> | null
  decided_by_username: string | null
  decided_at: string | null
  decision_note: string
  created_at: string
}

const RISK_COLOR: Record<Approval['tool_risk_level'], string> = {
  SAFE: 'bg-green-100 text-green-700',
  WRITE: 'bg-amber-100 text-amber-800',
  EXTERNAL: 'bg-red-100 text-red-700',
}

const STATUS_COLOR: Record<Approval['status'], string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  EDITED: 'bg-blue-100 text-blue-700',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-gray-100 text-gray-600',
}

// ─────────────────── Page ───────────────────

export default function AdminAIAgentApprovals() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const [filter, setFilter] = useState<'PENDING' | 'ALL'>('PENDING')

  const approvalsQ = useQuery({
    queryKey: ['ai-agents', 'approvals', filter],
    queryFn: async () =>
      (await api.get<Approval[]>(`/ai-agents/approvals/?status=${filter}`)).data,
    refetchInterval: 8_000,
  })

  const approvals = approvalsQ.data || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-muted-foreground" /> Agent approvals
          </h2>
          <p className="text-sm text-muted-foreground">
            Risky tool calls land here. Approve, edit, or reject — the parent run resumes when every approval is decided.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/ai-agents')}>← Agents</Button>
      </div>

      <div className="inline-flex gap-1 text-xs bg-muted/40 rounded p-0.5 mb-4">
        {(['PENDING', 'ALL'] as const).map((k) => (
          <button
            key={k} type="button"
            onClick={() => setFilter(k)}
            className={`px-3 py-1 rounded ${filter === k ? 'bg-card shadow font-semibold' : 'text-muted-foreground'}`}
          >
            {k === 'PENDING' ? 'Pending only' : 'All'}
          </button>
        ))}
      </div>

      {approvalsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : approvals.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          {filter === 'PENDING' ? 'No pending approvals — you\'re clear.' : 'No approvals yet.'}
        </div>
      ) : (
        <div className="space-y-3">
          {approvals.map((a) => (
            <ApprovalCard
              key={a.id}
              approval={a}
              onDecided={() => qc.invalidateQueries({ queryKey: ['ai-agents', 'approvals'] })}
            />
          ))}
        </div>
      )}
    </AdminLayout>
  )
}

// ─────────────────── One approval row ───────────────────

function ApprovalCard({ approval, onDecided }: { approval: Approval; onDecided: () => void }) {
  const [editing, setEditing] = useState(false)
  const [editedInput, setEditedInput] = useState(
    JSON.stringify(approval.proposed_input, null, 2),
  )
  const [note, setNote] = useState('')

  const decideMut = useMutation({
    mutationFn: async (payload: { decision: 'APPROVE' | 'EDIT' | 'REJECT'; final_input?: any; note?: string }) =>
      (await api.post<Approval>(`/ai-agents/approvals/${approval.id}/decide/`, payload)).data,
    onSuccess: (res) => {
      toast.success(`${res.status}`)
      onDecided()
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function approve() {
    decideMut.mutate({ decision: 'APPROVE', note })
  }
  function reject() {
    decideMut.mutate({ decision: 'REJECT', note })
  }
  function submitEdit() {
    let parsed: any
    try {
      parsed = JSON.parse(editedInput)
    } catch (e) {
      toast.error('Input is not valid JSON')
      return
    }
    decideMut.mutate({ decision: 'EDIT', final_input: parsed, note })
  }

  const pending = approval.status === 'PENDING'

  return (
    <div className="bg-card rounded-lg shadow p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">#{approval.id}</span>
            <span className="text-sm font-semibold">{approval.agent_name}</span>
            <span className="text-xs text-muted-foreground">wants to call</span>
            <span className="text-sm font-mono">{approval.tool_code}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${RISK_COLOR[approval.tool_risk_level]}`}>
              {approval.tool_risk_level}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${STATUS_COLOR[approval.status]}`}>
              {approval.status}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {new Date(approval.created_at).toLocaleString()} · run{' '}
            <Link to={`/admin/ai-agents/runs/${approval.run}`} className="text-violet-600 hover:underline">
              #{approval.run}
            </Link>
            {approval.decided_by_username && (
              <span> · decided by {approval.decided_by_username} at {approval.decided_at && new Date(approval.decided_at).toLocaleString()}</span>
            )}
          </div>
        </div>
        {pending && !editing && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Edit2 className="h-3 w-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline"
              onClick={reject} disabled={decideMut.isPending}
              className="text-destructive"
            >
              <X className="h-3 w-3 mr-1" /> Reject
            </Button>
            <Button size="sm"
              onClick={approve} disabled={decideMut.isPending}
            >
              <Check className="h-3 w-3 mr-1" /> Approve
            </Button>
          </div>
        )}
      </div>

      {approval.reasoning && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-muted-foreground mb-1">Agent reasoning</div>
          <div className="text-sm whitespace-pre-wrap bg-muted/40 rounded p-2">{approval.reasoning}</div>
        </div>
      )}

      <div className="mb-3">
        <div className="text-xs font-semibold text-muted-foreground mb-1">
          {editing ? 'Edit input (JSON)' : 'Proposed input'}
        </div>
        {editing ? (
          <Textarea
            rows={8} value={editedInput}
            onChange={(e) => setEditedInput(e.target.value)}
            className="font-mono text-sm"
          />
        ) : (
          <pre className="text-xs font-mono bg-muted/40 rounded p-2 overflow-x-auto">
            {JSON.stringify(approval.proposed_input, null, 2)}
          </pre>
        )}
      </div>

      {approval.status === 'EDITED' && approval.final_input && !editing && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-muted-foreground mb-1">Final input (after edit)</div>
          <pre className="text-xs font-mono bg-blue-50 rounded p-2 overflow-x-auto">
            {JSON.stringify(approval.final_input, null, 2)}
          </pre>
        </div>
      )}

      {pending && editing && (
        <>
          <div className="mb-3">
            <label className="text-xs font-semibold text-muted-foreground mb-1 block">
              Decision note (optional)
            </label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Why you edited / why you're rejecting" />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={submitEdit} disabled={decideMut.isPending}>
              <Check className="h-3 w-3 mr-1" /> Approve with edits
            </Button>
          </div>
        </>
      )}

      {approval.decision_note && !pending && (
        <div className="text-xs">
          <strong>Note:</strong> {approval.decision_note}
        </div>
      )}
    </div>
  )
}

function _Tools({ children }: { children?: ReactNode }) { return <>{children}</> }
